import { Component, Directive, Inject, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSelect } from "@angular/material/select";
import { AuthorizedUser, ConfigStorage, getFormErrorList, isObjectValidator, requiredIfValidator, UserStorage } from "@scisys-uim/uim-common";
import { AssetsUser, Firearm, FIREARM_ASSIGNABLE_PERSON_METADATA, FIREARM_ASSIGNABLE_PERSON_TYPE, FIREARM_STORAGE_LOCATION_METADATA, FIREARM_STORAGE_LOCATION_TYPE, FirearmAssignablePerson, FirearmAssignmentLog, FirearmPool, FirearmStorageLocation } from "assets-model";
import { BusinessUnitService, CostCentreService, FirearmAssignablePersonService, FirearmAssignmentLogService, FirearmPoolService, FirearmService, FirearmStorageLocationService } from "assets-service";
import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from "rxjs";
import { filter, map, tap } from "rxjs/operators";
import { AutocompleteStringFilterFunctionAsync } from "../../../shared/form/autocomplete.util";



export interface FirearmStorageAssignmentCreationDialogData {
  storageAssignee?: FirearmAssignablePerson;
  location?: FirearmStorageLocation;
  comments?: string;
  firearm?: Firearm;
}

export interface FirearmStorageAssignmentCreationDialogConfigData {
  firearm?: Firearm;
  storageAssignee?: FirearmAssignablePerson;
}

@Directive()
export abstract class FirearmStorageAssignmentCreationDialogComponent implements OnInit {

  protected systemConfig: any;

  public form: FormGroup;

  public item: FirearmStorageAssignmentCreationDialogData = {};

  protected userDetails: AuthorizedUser;
  firearmAssignmentLog: FirearmAssignmentLog;
  firearmPool: FirearmPool;

  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  get hasPendingActions(): boolean {
    return this.firearmAssignmentLog?.pendingActions;
  }

  get currentlyIssued(): boolean {
    return this.data?.firearm?.currentAssignmentFirearmAssignmentType == 'ISSUE';
  }

  private _onInitCompleteSubject: Subject<boolean> = new BehaviorSubject<boolean>(null);
  $onInitComplete: Observable<boolean> = this._onInitCompleteSubject.asObservable().pipe(filter(o => o != null));
  private onInitSubscription: Subscription = null;

  @ViewChild('storageAssignee') storageAssigneeInput: MatSelect;
  storageAssigneeSelectionList: FirearmAssignablePerson[] = [];

  @ViewChild('location') locationInput: MatSelect;
  locationSelectionList: FirearmStorageLocation[] = [];

  constructor(
    protected dialogRef: MatDialogRef<FirearmStorageAssignmentCreationDialogComponent, FirearmStorageAssignmentCreationDialogData>,
    protected data: FirearmStorageAssignmentCreationDialogConfigData,
    protected fb: FormBuilder,
    protected dialog: MatDialog,
    protected firearmService: FirearmService,
    protected firearmAssignmentLogService: FirearmAssignmentLogService,
    protected firearmAssignablePersonService: FirearmAssignablePersonService,
    protected firearmStorageLocationService: FirearmStorageLocationService,
    protected firearmPoolService: FirearmPoolService,
    protected configService: ConfigStorage,
    protected userStorage: UserStorage) {

    dialogRef.disableClose = true;

    this.data = this.data || {
    };

    this.form = this.fb.group({
      storageAssignee: [null, [isObjectValidator,]],
      location: [null],
      comments: [null]
    }, {
      validators: [
        requiredIfValidator(() => this.storageAssignee, () => this.isStorageAssigneeRequired(), "Storage Assignee", { required: "Storage Assignee is required for storage of issued firearm." }),
        requiredIfValidator(() => this.location, () => this.isLocationRequired(), "Location", { required: "Location is required for storage of current firearms." }),
        requiredIfValidator(() => this.comments, () => this.isCommentRequired(), "Comment", { required: "Storage Assignment Comment is required for storage of current firearms." }),
      ]
    });

    this.firearmAssignmentLogService.get(data.firearm?.asset?.assetId).subscribe(result => {
      this.firearmAssignmentLog = result;
    });

    this.item.storageAssignee = this.data.storageAssignee;
    if (data.firearm?.poolFirearm) {
      this.firearmPoolService.get(data.firearm?.asset?.assetId).subscribe(result => {
        this.firearmPool = result;
        this.item.storageAssignee = null;
        this.storageAssigneeLoadSelectionList();
      });
    }

    this.setDefaultDisabledFields();
  }

  protected onInitAsyncComplete() {
    this.storageAssigneeLoadSelectionList();
    this.locationLoadSelectionList();
  }

  protected abstract isStorageAssigneeRequired();
  protected abstract isLocationRequired();
  protected abstract isCommentRequired();

  ngAfterViewInit() {
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (this.data.storageAssignee != null) {
      this.storageAssignee.disable();
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  isFirearmStorageAssignmentCreationEnabled() {
    return !this.hasPendingActions && !this.form.invalid;
  }

  getFormErrorList() {
    var result = [...getFormErrorList(this.form)];
    return result;
  }

  // Person field


  storageAssigneeLoadSelectionList() {
    this.firearmAssignablePersonService.findByFullFilters(this.createStorageAssigneeFilter()).subscribe(
      data => {
        this.storageAssigneeSelectionList = FIREARM_ASSIGNABLE_PERSON_TYPE.entityDecorator.ensureContained(this.item && this.item.storageAssignee, data.items);
        if (this.item.storageAssignee == null && this.storageAssigneeSelectionList?.length == 1) {
          this.item.storageAssignee = this.storageAssigneeSelectionList[0];
        }
      }
    );
  }

  protected createStorageAssigneeFilter(): any {
    const filters = <any>{
      buId: this.data.firearm?.asset?.businessUnit?.buId,
      assignmentType: 'STORAGE'
    };
    if (this.firearmPool != null) {
      filters.user = this.firearmPool.manager?.user;
    }
    return filters;
  }

  storageAssigneeDisplay(item: FirearmAssignablePerson) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_ASSIGNABLE_PERSON_TYPE.description(item);
  }

  storageAssigneeChanging(value: FirearmAssignablePerson, unsetDependents: boolean = true) { }

  storageAssigneeChanged(value: FirearmAssignablePerson, unsetDependents: boolean = true) {
    this.locationLoadSelectionList();
  }

  storageAssigneeCompare(a: any, b: any) {
    return FIREARM_ASSIGNABLE_PERSON_METADATA.equals(a, b);
  }

  get storageAssignee() {
    return this.control('storageAssignee');
  }

  // Location field

  locationLoadSelectionList() {
    this.firearmStorageLocationService.findByFullFilters(this.createLocationFilter()).subscribe(
      data => {
        this.locationSelectionList = FIREARM_STORAGE_LOCATION_TYPE.entityDecorator.ensureContained(this.item && this.item.location, data.items);
        this.locationSelectionListLoaded();
      }
    );
  }

  protected createLocationFilter(): any {
    return {
      includeNational: true,
      buId: this.data.firearm?.asset?.businessUnit?.buId,
    };
  }

  locationSelectionListLoaded() {
  }

  locationDisplay(item: FirearmStorageLocation) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_STORAGE_LOCATION_TYPE.description(item);
  }

  locationChanging(value: string, unsetDependents: boolean = true) { }

  locationChanged(value: string, unsetDependents: boolean = true) {
  }

  locationCompare(a: any, b: any) {
    return FIREARM_STORAGE_LOCATION_METADATA.equals(a, b);
  }


  get location() {
    return this.control('location');
  }

  // Comments field
  commentsChanging(value: string, unsetDependents: boolean = true) { }

  commentsChanged(value: string, unsetDependents: boolean = true) {
  }

  get comments() {
    return this.control('comments');
  }

  // form controls general

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  // 'framework'

  ngOnInit() {
    this.onInitAsync();
  }


  protected onInitAsync() {
    const allObservables: Observable<any>[] = this.onInitAsyncObservables() || [];
    this.onInitSubscription = combineLatest(allObservables).subscribe(() => {
      this.onInitSubscription?.unsubscribe();
      this._onInitAsyncComplete();
    });
  }

  protected _onInitAsyncComplete() {
    this.onInitAsyncComplete();
    this.configService.itemChanges().subscribe((configData: any) => this.initConfig(configData));
    this._onInitCompleteSubject.next(true);
    this._onInitCompleteSubject.complete();
  }

  // These observables will be waited for, on page load, before the screen is ready to search.
  // These Observables are required by the framework - take care in overriding
  protected onInitAsyncObservables(): Observable<any>[] {
    return [this.onInitConfigObservable(),
    this.onInitUserDetails()];
  }
  protected onInitConfigObservable(): Observable<any> {
    return this.configService.resolve().pipe(tap((configData: any) => this.initConfig(configData)));
  }
  protected initConfig(config: any) {
    this.systemConfig = config || {};
  }
  protected onInitUserDetails(): Observable<AuthorizedUser> {
    return this.userStorage.nextNonNullItem().pipe(tap((userDetails: AuthorizedUser) => this.initUserDetails(userDetails)));
  }
  protected initUserDetails(userDetails: AuthorizedUser) {
    this.userDetails = userDetails;
  }


  disabledTooltip(element) {
    return element && (element.valid || !element.touched || !element.value);
  }
}